import classNames from 'classnames';
import { MouseEvent, ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { HeroBadge } from './components/HeroBadge';
import { HeroStarfield } from './components/HeroStarfield';

// Download glyph shown in the badge's blue chip (matches Section8's badge style).
const DownloadGlyph = () => (
  <svg viewBox="0 0 24 24" className={styles.badgeGlyph} aria-hidden="true">
    <path d="M12 3v11M8 11l4 4 4-4M5 20h14" />
  </svg>
);

interface PlatformLink {
  /** Target section id on the page. */
  id: string;
  /** i18n key (under pages.download.hero) for the link label. */
  labelKey: string;
  icon: ReactNode;
  /** Filled glyphs (Apple, Android) paint with fill instead of stroke. */
  filled?: boolean;
}

const PLATFORMS: PlatformLink[] = [
  {
    id: 'web',
    labelKey: 'pages.download.hero.linkWeb',
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
      </>
    ),
  },
  // {
  //   id: 'chrome',
  //   labelKey: 'pages.download.hero.linkChrome',
  //   icon: (
  //     <>
  //       <circle cx="12" cy="12" r="9" />
  //       <circle cx="12" cy="12" r="3.4" />
  //       <path d="M12 8.6h9M8.6 13.7l-4.5 7.8M15.4 13.7l-4.5 7.8" />
  //     </>
  //   ),
  // },
  {
    id: 'ios',
    labelKey: 'pages.download.hero.linkIos',
    filled: true,
    icon: (
      <path d="M16.5 1.6c.1 1-.3 2-.9 2.8-.7.8-1.7 1.4-2.7 1.3-.1-1 .4-2 1-2.7.6-.8 1.7-1.3 2.6-1.4zM19.6 17c-.5 1.2-.8 1.7-1.4 2.7-.9 1.4-2.2 3.2-3.8 3.2-1.4 0-1.8-.9-3.7-.9s-2.4.9-3.7.9c-1.6 0-2.8-1.6-3.7-3C.8 16.6.5 11.4 2.6 8.6c1-1.4 2.5-2.2 4-2.2 1.5 0 2.5 1 3.8 1 1.2 0 2-1 3.8-1 1.3 0 2.7.7 3.7 2-3.3 1.8-2.7 6.4 1.7 8.6z" />
    ),
  },
  {
    id: 'android',
    labelKey: 'pages.download.hero.linkAndroid',
    filled: true,
    icon: (
      <path d="M5 16a1 1 0 0 0 1 1h1v3a1 1 0 0 0 2 0v-3h6v3a1 1 0 0 0 2 0v-3h1a1 1 0 0 0 1-1V9H5zM3 9.5a1 1 0 0 1 2 0v4a1 1 0 0 1-2 0zM19 9.5a1 1 0 0 1 2 0v4a1 1 0 0 1-2 0zM7.6 8a4.4 4.4 0 0 1 8.8 0z" />
    ),
  },
  {
    id: 'macos',
    labelKey: 'pages.download.hero.linkMacos',
    icon: (
      <>
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <path d="M2 20h20M9.5 20l.4-4M14.5 20l-.4-4" />
      </>
    ),
  },
];

// Download page hero (Section41). Built on the homepage hero (Section8) — same
// starfield, badge, title and subtitle styling — but the CTA row is replaced
// with links that smooth-scroll to each platform section further down the page.
export const Section41 = () => {
  const t = useTranslations();
  const jumpTo = (id: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className={styles.section}>
      {/* Decorative animated starfield (paints the navy bg + glow itself).
          Kept mounted (visibility, not display) so its canvas keeps a real
          size across theme flips. */}
      <HeroStarfield className="invisible marketing-dark:visible" />

      {/* Light-theme glow: soft pastel-blue blobs behind the hero copy. */}
      <div className={styles.lightGlow} aria-hidden="true" />

      <div className={styles.wrap}>
        <HeroBadge
          icon={<DownloadGlyph />}
          iconClassName="bg-mf-blue text-white"
          label={t('pages.download.hero.badge')}
        />
        <h1 className={styles.title}>
          {t('pages.download.hero.title')}
          <br />
          {t.rich('pages.download.hero.titleLine2', {
            highlight: (chunks) => (
              <span className={styles.emphasis}>{chunks}</span>
            ),
          })}
        </h1>
        <p className={styles.sub}>{t('pages.download.hero.sub')}</p>

        <div className={styles.jump}>
          {PLATFORMS.map(({ id, labelKey, icon, filled }) => (
            <a
              key={id}
              href={`#${id}`}
              onClick={jumpTo(id)}
              className={styles.link}
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                className={filled ? styles.iconFilled : styles.icon}
              >
                {icon}
              </svg>
              {t(labelKey)}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

const styles = {
  // pt clears the fixed header stack (announcement bar + nav).
  section:
    'relative overflow-hidden marketing-dark:bg-mf-navy text-center pt-[160px] pb-[70px]',
  lightGlow: classNames(
    'pointer-events-none absolute left-1/2 top-[-120px] h-[700px] w-[1400px] max-w-none -translate-x-1/2',
    'bg-[radial-gradient(46%_58%_at_34%_0%,rgba(189,222,255,0.6)_0%,transparent_65%),radial-gradient(40%_52%_at_70%_4%,rgba(189,222,255,0.45)_0%,transparent_62%)]',
    'marketing-dark:hidden',
  ),
  wrap: 'relative z-[2] max-w-[1200px] mx-auto px-6 lg:px-10',
  badgeGlyph: 'h-3 w-3 fill-none stroke-current stroke-2',
  title: classNames(
    'mx-auto mb-[22px] max-w-[980px] font-bold tracking-[-0.02em]',
    'text-mf-off-black-600 marketing-dark:text-white',
    'text-4xl leading-tight sm:text-5xl lg:text-[64px] lg:leading-[1.04]',
  ),
  emphasis: 'text-mf-blue marketing-dark:text-mf-coral',
  sub: 'mx-auto mb-[30px] max-w-[600px] text-lg leading-relaxed text-mf-gray-600 marketing-dark:text-white/72 lg:text-xl',
  jump: 'flex flex-wrap justify-center gap-[10px]',
  link: classNames(
    'inline-flex items-center gap-2 rounded-full border px-4 py-[9px] text-[13.5px] font-semibold',
    'transition-[background-color,border-color,transform] hover:-translate-y-0.5',
    'border-[#DADADA] bg-white text-mf-gray-600 hover:border-mf-gray-500 hover:bg-[#F4F4F4]',
    'marketing-dark:border-white/[0.14] marketing-dark:bg-white/[0.06] marketing-dark:text-white/84',
    'marketing-dark:hover:border-white/30 marketing-dark:hover:bg-white/10',
  ),
  icon: 'h-[15px] w-[15px] flex-shrink-0 fill-none stroke-current stroke-[1.9]',
  iconFilled: 'h-[15px] w-[15px] flex-shrink-0 fill-current stroke-none',
};
